import { useState } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  UserIdentity,
} from '@backstage/core-components';
import { githubAuthApiRef, useApi, SignInPageProps } from '@backstage/core-plugin-api';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { AwsHealthMonitorPage } from '@internal/plugin-aws-health-monitor';
import { AwsCloudTrailPage } from '@internal/plugin-aws-cloudtrail-monitor';
import { TaskBoardPage } from '@internal/plugin-task-board';
import { AwsBillingPage } from '@internal/plugin-aws-billing';
import { NotificationPreferencesPage } from '@internal/plugin-notification-preferences';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import logo from './wootic_logo.jpg';

// ── Custom sign-in page ───────────────────────────────────────────────────────

const WooticSignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const githubAuth = useApi(githubAuthApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const identityResponse = await githubAuth.getBackstageIdentity({ instantPopup: true });
      const profile = await githubAuth.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse!.identity,
          authApi: githubAuth,
          profile: profile ?? {},
        }),
      );
    } catch (e: any) {
      setError('No se pudo iniciar sesión. Verificá que tu cuenta pertenece a la organización Wootic.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0d1f3c',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'sans-serif',
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
        maxWidth: 380,
        width: '90%',
        backgroundColor: 'rgba(255,255,255,0.07)',
        borderRadius: 16,
        padding: '48px 40px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}>
        <img src={logo} alt="WOOTIC" style={{ width: 88, borderRadius: 12 }} />
        <div style={{ textAlign: 'center' }}>
          <Typography variant="h5" style={{ color: '#fff', fontWeight: 700, marginBottom: 8 }}>
            WOOTIC Backstage
          </Typography>
          <Typography style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', lineHeight: 1.65 }}>
            Iniciá sesión con tu cuenta de GitHub. Solo podrán acceder los miembros de la organización Wootic.
          </Typography>
        </div>
        {error && (
          <Typography style={{ color: '#f48fb1', fontSize: '0.8rem', textAlign: 'center' }}>
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleSignIn}
          disabled={loading}
          style={{
            backgroundColor: '#24292e',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            padding: '11px 0',
            borderRadius: 8,
            marginTop: 4,
          }}
        >
          {loading
            ? <><CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} /> Conectando…</>
            : 'Continuar con GitHub'}
        </Button>
      </div>
    </div>
  );
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <WooticSignInPage {...props} />,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/aws-health-monitor" element={<AwsHealthMonitorPage />} />
    <Route path="/aws-cloudtrail-monitor" element={<AwsCloudTrailPage />} />
    <Route path="/task-board" element={<TaskBoardPage />} />
    <Route path="/aws-billing" element={<AwsBillingPage />} />
    <Route path="/mis-notificaciones" element={<NotificationPreferencesPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
